import { useState, useEffect } from 'react';
import { getTemplates, updateTemplate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Mail, 
  Edit2, 
  Eye,
  Star,
  Check
} from 'lucide-react';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setEditSubject(template.subject);
    setShowEditModal(true);
  };

  const openPreviewModal = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleSaveSubject = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await updateTemplate(selectedTemplate.id, {
        name: selectedTemplate.name,
        subject: editSubject,
        body: selectedTemplate.body,
        is_default: selectedTemplate.is_default
      });
      toast.success('Email subject updated');
      setShowEditModal(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  // Generate a preview with sample data
  const getPreviewHtml = (template) => {
    if (!template) return '';
    let html = template.body;
    html = html.replace(/\{\{customer_name\}\}/g, 'John Smith');
    html = html.replace(/\{\{business_name\}\}/g, 'Your Business');
    html = html.replace(/\{\{review_link\}\}/g, '#');
    html = html.replace(/\{\{track_link_1\}\}/g, '#');
    html = html.replace(/\{\{track_link_2\}\}/g, '#');
    html = html.replace(/\{\{track_link_3\}\}/g, '#');
    html = html.replace(/\{\{track_link_4\}\}/g, '#');
    html = html.replace(/\{\{track_link_5\}\}/g, '#');
    html = html.replace(/\{\{rating_link_1\}\}/g, '#');
    html = html.replace(/\{\{rating_link_2\}\}/g, '#');
    html = html.replace(/\{\{rating_link_3\}\}/g, '#');
    html = html.replace(/\{\{rating_link_4\}\}/g, '#');
    html = html.replace(/\{\{rating_link_5\}\}/g, '#');
    return html;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="h-48 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground tracking-tight">Email Templates</h1>
          <p className="text-muted-foreground mt-1">
            Your review request email templates
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20 animate-fade-in">
        <CardContent className="py-4">
          <p className="text-sm text-foreground">
            <strong>How it works:</strong> When you send review requests, the email will automatically include your business name and customer's name. 
            Customers click the emoji that matches their experience, which takes them to your Google Reviews page.
          </p>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {templates.map((template) => (
          <Card 
            key={template.id} 
            className="hover:shadow-md transition-shadow duration-200 animate-fade-in"
            data-testid={`template-card-${template.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      {template.name}
                      {template.is_default && (
                        <Badge className="bg-[#D9F99D] text-[#1A3C34] border-0 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </Badge>
                      )}
                    </CardTitle>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Subject Line */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Subject Line:</p>
                <p className="text-sm font-medium text-foreground">{template.subject}</p>
              </div>

              {/* Email Preview Thumbnail */}
              <div className="bg-white border rounded-lg p-4 max-h-48 overflow-hidden relative">
                <div 
                  className="text-xs transform scale-75 origin-top-left"
                  style={{ width: '133%' }}
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml(template) }}
                />
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openPreviewModal(template)}
                  className="btn-animate"
                  data-testid={`preview-template-${template.id}`}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditModal(template)}
                  className="btn-animate"
                  data-testid={`edit-template-${template.id}`}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit Subject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <Card className="col-span-full">
            <div className="empty-state">
              <Mail className="empty-state-icon" />
              <h3 className="empty-state-title">No templates yet</h3>
              <p className="empty-state-description">
                Your default email template will be created automatically.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Edit Subject Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Email Subject</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSubject} className="space-y-5">
            <div className="form-group">
              <Label htmlFor="subject">Subject Line</Label>
              <Input
                id="subject"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                placeholder="e.g., We'd love your feedback, {{customer_name}}!"
                required
                data-testid="template-subject-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Use <code className="bg-muted px-1 rounded">{'{{customer_name}}'}</code> to include the customer's name
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-template-btn">
                <Check className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              {/* Subject Preview */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                <p className="text-sm font-medium">
                  {selectedTemplate.subject.replace('{{customer_name}}', 'John Smith')}
                </p>
              </div>

              {/* Email Body Preview */}
              <div className="border rounded-lg p-4 bg-white">
                <div 
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml(selectedTemplate) }}
                />
              </div>

              <p className="text-xs text-muted-foreground text-center">
                This is a preview with sample data. Actual emails will use real customer names and your business name.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
